/* eslint-disable sonarjs/no-duplicate-string */
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_BUYER_DASHBOARD_PLOT_REPORT_PATH,
  GET_DASHBOARD_PLOT_REPORT_PATH,
  GetBuyerDashboardPlotReportErrorDto,
  GetBuyerDashboardPlotReportRequestQueryParams,
  GetBuyerDashboardPlotReportSuccessDto,
  GetDashboardPlotReportErrorDto,
  GetDashboardPlotReportRequestQueryParams,
  GetDashboardPlotReportSuccessDto,
} from '@/api/rest/resources/types/dashboard';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { Plot } from '@/api/rest/resources/types/plot';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useMembershipType } from '@/hooks/useMembershipType';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';

type QueryParams = GetDashboardPlotReportRequestQueryParams | GetBuyerDashboardPlotReportRequestQueryParams;
type SuccessDto = GetDashboardPlotReportSuccessDto | GetBuyerDashboardPlotReportSuccessDto;
type ErrorDto = GetDashboardPlotReportErrorDto | GetBuyerDashboardPlotReportErrorDto;

export const usePlotReportForPlot = (plotId: string, queryParamsArg?: QueryParams) => {
  const membershipType = useMembershipType();

  const pathVariables = { plot_id: plotId };

  const plotGroupId = usePlotGroupId();
  const workstreamId = useWorkstreamId();
  const queryParams = {
    plot_group_id: plotGroupId,
    ...(workstreamId ? { workstream_id: workstreamId } : {}),
    ...(queryParamsArg || {}),
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(
      membershipType === MembershipWithOrganizationTypeEnum.land_steward
        ? GET_DASHBOARD_PLOT_REPORT_PATH
        : GET_BUYER_DASHBOARD_PLOT_REPORT_PATH,
      pathVariables,
      queryParams,
    );
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: [membershipType, 'dashboard', 'plot-report', pathVariables, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const usePlotReportBatchRequest = (plotId: string, queryParamsArray: QueryParams[]) => {
  const membershipType = useMembershipType();
  const plotGroupId = usePlotGroupId();
  const workstreamId = useWorkstreamId();

  const pathVariables = { plot_id: plotId };

  return useSuspenseQueries({
    queries: queryParamsArray.map((queryParamsArg) => {
      const queryParams = {
        plot_group_id: plotGroupId,
        ...(workstreamId ? { workstream_id: workstreamId } : {}),
        ...(queryParamsArg || {}),
      };
      return {
        queryKey: [membershipType, 'dashboard', 'plot-report', pathVariables, queryParams],
        queryFn: async () =>
          getRequest<SuccessDto>(
            membershipType === MembershipWithOrganizationTypeEnum.land_steward
              ? GET_DASHBOARD_PLOT_REPORT_PATH
              : GET_BUYER_DASHBOARD_PLOT_REPORT_PATH,
            pathVariables,
            queryParams,
          ),
        staleTime: 1000 * 60 * 5,
      };
    }),
  });
};

export const usePlotReportForPlots = (plots: Plot[], queryParamsArg?: QueryParams) => {
  const membershipType = useMembershipType();

  return useSuspenseQueries({
    queries: plots.map((plot) => ({
      queryKey: [membershipType, 'dashboard', 'plot-report', { plot_id: plot.id }, queryParamsArg],
      queryFn: async () => {
        return getRequest<GetDashboardPlotReportSuccessDto | GetBuyerDashboardPlotReportSuccessDto>(
          membershipType === MembershipWithOrganizationTypeEnum.land_steward
            ? GET_DASHBOARD_PLOT_REPORT_PATH
            : GET_BUYER_DASHBOARD_PLOT_REPORT_PATH,
          { plot_id: plot.id },
          queryParamsArg,
        );
      },
      staleTime: 1000 * 60 * 5,
    })),
  });
};
